import { PlanWindowSource } from 'src/components/dispatch/vehicleRowExpand/buildExpandedRowVM';
import { PlanningsDeliveryWithLegRunEntity } from 'src/entities/PlanningsDelivery.entity';

const toDateOrNull = (iso: string | undefined | null): Date | null => {
  if (!iso) return null;
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? null : date;
};

/** operation の端。旧マップは workStartAt / workEndAt が無い勤務をこれで代用している */
function operationEdges(operations: readonly { arrivalAt: string; departureAt: string }[]) {
  let minArrival: number | null = null;
  let maxDeparture: number | null = null;
  for (const operation of operations) {
    const arrival = toDateOrNull(operation.arrivalAt)?.getTime();
    const departure = toDateOrNull(operation.departureAt)?.getTime();
    if (arrival != null && (minArrival == null || arrival < minArrival)) minArrival = arrival;
    if (departure != null && (maxDeparture == null || departure > maxDeparture)) maxDeparture = departure;
  }
  return {
    minArrival: minArrival == null ? null : new Date(minArrival),
    maxDeparture: maxDeparture == null ? null : new Date(maxDeparture),
  };
}

/**
 * 展開行ヘッダーの「計画」に出す勤務 (delivery) 全体の窓。
 *
 * 新マップは「勤務開始時刻 〜 その回転の最終地点の出発」を出していたため、旧マップ (/plans) の
 * 「計画 05:35 ~ 14:51」に対して「03:00〜12:31」と食い違っていた (log_web_issues#310)。
 * 旧マップ PlanningsDelivery.presenter.tsx:305-323 と同じ値をここで作る。
 *
 * - 配車 (operation) が無い勤務は窓を作らない（旧地図も計画を出さない）
 * - 開始 = workStartAt（API では departure_operations.arrival_at = 出庫作業の開始）
 * - 終了 = workEndAt（API では arrival_operations.departure_at = 帰庫作業の終了）
 * - どちらも無い勤務は operation の端で代用する
 * - アイドル時間は所要から差し引くため合計して持たせる
 *
 * 1 delivery は cycle ごとに複数 row になる（vehicleRowVM.ts:202-209）ので、delivery 単位で
 * 作った窓を全 row へ配る。旧マップに回転単位の計画表示は無く、回転ごとに切ると
 * 突き合わせる相手が無い。
 */
export function buildDeliveryPlanWindows(
  deliveries: readonly PlanningsDeliveryWithLegRunEntity[],
): Map<number, PlanWindowSource> {
  const map = new Map<number, PlanWindowSource>();
  for (const delivery of deliveries) {
    const operations = delivery.operations ?? [];
    // 配車ゼロの勤務には計画を出さない。旧地図も operation が無い勤務では計画そのものを
    // 出しておらず (PlanningsDelivery.presenter.tsx:903)、出庫・帰庫だけ引けても
    // 「何の計画なのか」が無い。行は --:--~--:-- に落ちる
    if (operations.length === 0) continue;

    const { minArrival, maxDeparture } = operationEdges(operations);
    const startAt = toDateOrNull(delivery.workStartAt) ?? minArrival;
    const endAt = toDateOrNull(delivery.workEndAt) ?? maxDeparture;
    // 配車前の勤務は窓を作れない。行ごとのフォールバックに任せる（仮の値で埋めない）
    if (!startAt && !endAt) continue;

    // 旧マップと同じく 0 より大きいものだけ足す（負値を所要の引き算に持ち込まない）
    const idleSeconds = operations.reduce(
      (total, operation) => (operation.nullDurationSeconds > 0 ? total + operation.nullDurationSeconds : total),
      0,
    );
    map.set(delivery.id, { startAt, endAt, idleSeconds });
  }
  return map;
}
